import { API_BASE_URL } from "./api-client";

/**
 * WebSocket URL for a mailbox's real-time event stream.
 *
 * `VITE_WS_BASE_URL` may be an API origin (`ws://localhost:8787`) or the full
 * base (`ws://localhost:8787/api/v1`); the `/api/v1` suffix is appended when
 * absent. When unset, the URL is derived from `API_BASE_URL`.
 */
export function mailboxWebSocketUrl(mailboxId: string): string {
  const configured = import.meta.env.VITE_WS_BASE_URL;
  if (configured !== undefined && configured.trim().length > 0) {
    const trimmed = configured.trim().replace(/\/+$/u, "");
    const base = /\/api\/v1$/u.test(trimmed) ? trimmed : `${trimmed}/api/v1`;
    return `${base}/ws/mailboxes/${mailboxId}`;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const base = new URL(API_BASE_URL, origin);
  const protocol = base.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${base.host}${base.pathname}/ws/mailboxes/${mailboxId}`;
}

const MIN_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 30_000;

/** Cryptographically-random integer in `[0, max)`. */
function secureRandom(max: number): number {
  if (max <= 0) return 0;
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return (buffer[0] ?? 0) % max;
}

/**
 * Exponential backoff with full jitter: `random(0, min(cap, base * 2^attempt))`.
 * Jitter prevents reconnect storms when a mailbox room restarts.
 */
export function nextBackoffMs(attempt: number): number {
  const exponential = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** Math.min(attempt, 10));
  return MIN_BACKOFF_MS + secureRandom(Math.max(1, exponential - MIN_BACKOFF_MS));
}
